import type { ProjectExtensions } from '@/types';

export type Sb3ProjectPayload = {
  formatVersion?: string;
  workspaceXml?: string;
  name?: string;
  description?: string;
  version?: string;
  thumbnail?: string;
  extensions?: Pick<ProjectExtensions, 'peripherals' | 'controller' | 'ui' | 'stage'>;
  scratchImport?: {
    convertedBlocks: number;
    unsupportedOpcodes: string[];
  };
};

export const CURRENT_SB3_FORMAT_VERSION = '0.3';
export const YIBLOCK_PROJECT_FILE_EXTENSION = '.yiblock2';
export const YIBLOCK_PROJECT_FILE_DESCRIPTION = 'yiBlock2 项目文件';
export const LEGACY_PROJECT_FILE_EXTENSIONS = ['.sb3', '.json', '.yiblock'] as const;
export const SUPPORTED_PROJECT_FILE_EXTENSIONS = [
  YIBLOCK_PROJECT_FILE_EXTENSION,
  ...LEGACY_PROJECT_FILE_EXTENSIONS,
] as const;
export const PROJECT_FILE_ACCEPT = SUPPORTED_PROJECT_FILE_EXTENSIONS.join(',');

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

export const buildSb3ExtensionsPayload = (
  extensions: ProjectExtensions | undefined,
): NonNullable<Sb3ProjectPayload['extensions']> => {
  const selectedIds = Array.isArray(extensions?.peripherals?.selectedIds)
    ? Array.from(
        new Set(
          extensions.peripherals.selectedIds.filter(
            (id): id is string => typeof id === 'string' && id.trim().length > 0,
          ),
        ),
      )
    : [];
  const controllerModel =
    typeof extensions?.controller?.model === 'string' && extensions.controller.model.trim().length > 0
      ? extensions.controller.model
      : 'PC';
  const controllerIsLocal =
    typeof extensions?.controller?.isLocal === 'boolean' ? extensions.controller.isLocal : true;
  const payload: NonNullable<Sb3ProjectPayload['extensions']> = {
    peripherals: {
      selectedIds,
    },
    controller: {
      model: controllerModel,
      isLocal: controllerIsLocal,
    },
  };

  if (extensions?.ui?.mode === 'lvgl') {
    payload.ui = { mode: 'lvgl' };
  }
  if (extensions?.stage?.version === 1) {
    payload.stage = extensions.stage;
  }

  return payload;
};

export const parseSb3ExtensionsPayload = (
  value: unknown,
): Sb3ProjectPayload['extensions'] | undefined => {
  if (!isRecord(value)) {
    return undefined;
  }

  const payload: NonNullable<Sb3ProjectPayload['extensions']> = {};
  const controller = isRecord(value.controller) ? value.controller : undefined;
  const peripherals = isRecord(value.peripherals) ? value.peripherals : undefined;
  const ui = isRecord(value.ui) ? value.ui : undefined;
  const stage = isRecord(value.stage) ? value.stage : undefined;

  if (controller) {
    payload.controller = {
      model:
        typeof controller.model === 'string' && controller.model.trim().length > 0
          ? controller.model
          : 'PC',
      isLocal: typeof controller.isLocal === 'boolean' ? controller.isLocal : true,
    };
  }

  if (peripherals) {
    payload.peripherals = {
      selectedIds: Array.isArray(peripherals.selectedIds)
        ? Array.from(
            new Set(
              peripherals.selectedIds.filter(
                (id): id is string => typeof id === 'string' && id.trim().length > 0,
              ),
            ),
          )
        : [],
    };
  }

  if (ui?.mode === 'lvgl') {
    payload.ui = { mode: 'lvgl' };
  }
  if (stage?.version === 1) {
    payload.stage = stage as unknown as NonNullable<ProjectExtensions['stage']>;
  }

  return Object.keys(payload).length > 0 ? payload : undefined;
};